import type {ReactElement} from 'react';
import React, { useEffect} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {ControlledList} from '@Components/controlled-list';
import type { ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE, ControlledListItemSize} from '@Components/controlled-list/controlled-list.types';
import {copyPoster, onClickOpenADesign, openNewDesign} from '@Components/poster-editor/library/poster-editor-library';
import {onOpenLanguageModal, onOpenResizeModal, onOpenSettingsModal, openCollaboratorsModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {loadLanguages} from '@Components/language-selector/language-library';
import {isUserStudent} from "@Libraries/user.library";
import styles from './poster-editor-nav-more-options-panel.module.scss';
import {useAppSelector} from '@/hooks';

interface PosterEditorNavMoreOptionsPanelProps {
  panelId: string;
}

enum NavMoreOptions {
  CREATE_NEW = 'create_new',
  OPEN_DESIGN = 'open_design',
  RESIZE = 'resize',
  DUPLICATE = 'duplicate',
  COLLABORATE = 'collaborate',
  SETTINGS = 'settings',
  LANGUAGE = 'language',
  HELP = 'help',
}

export default function PosterEditorNavMoreOptionsPanel({...props}: PosterEditorNavMoreOptionsPanelProps): ReactElement {
  const onClose = useDefaultPanelClose(props.panelId);
  const langData = useAppSelector((state) => {
    return state.langSelector.pmwLangs;
  });

  useEffect(() => {
    void loadLanguages();
  }, []);

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_more_options')} />;
  };

  const getLanguageText = (): string => {
    if (!langData || !langData.length) {
      return window.i18next.t('pmwjs_loading');
    }

    const selectedLanguageData = langData.filter((item) => {
      if (item.code === window.i18next.language) {
        return 1;
      }
      return 0;
    });

    return selectedLanguageData[0].displayName;
  };

  const getItems = (): Array<ControlledListItemType> => {
    const items: Array<ControlledListItemType> = [
      {
        id: NavMoreOptions.RESIZE,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        text: window.i18next.t('pmwjs_resize'),
        icon: 'icon-resize',
        listSize: ControlledListItemSize.SMALL,
        className: styles.marginBottom,
      },
      {
        id: NavMoreOptions.DUPLICATE,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        text: window.i18next.t('pmwjs_duplicate_design'),
        icon: 'icon-duplicate',
        listSize: ControlledListItemSize.SMALL,
        className: styles.marginBottom,
      },
      {
        id: 'divider-1',
        type: CONTROLLED_LIST_ITEM_TYPE.DIVIDER,
        className: 'spacing-p-b-0',
      },
      {
        id: NavMoreOptions.CREATE_NEW,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        text: window.i18next.t('pmwjs_header_create_a_new_design'),
        icon: 'icon-new-design',
        className: styles.marginBottom,
        listSize: ControlledListItemSize.SMALL,
      },
      {
        id: NavMoreOptions.OPEN_DESIGN,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        text: window.i18next.t('pmwjs_go_to_my_stuff'),
        icon: 'icon-multiple-layouts',
        className: styles.marginBottom,
        listSize: ControlledListItemSize.SMALL,
      },
      {
        id: 'divider-2',
        type: CONTROLLED_LIST_ITEM_TYPE.DIVIDER,
        className: 'spacing-p-b-0',
      },
      {
        id: NavMoreOptions.SETTINGS,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        text: window.i18next.t('pmwjs_settings'),
        icon: 'icon-settings',
        className: styles.marginBottom,
        listSize: ControlledListItemSize.SMALL,
      },
      {
        id: NavMoreOptions.LANGUAGE,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        text: getLanguageText(),
        className: styles.marginBottom,
        icon: 'icon-translation',
        listSize: ControlledListItemSize.SMALL,
      },
      {
        id: NavMoreOptions.HELP,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        text: window.i18next.t('pmwjs_help'),
        className: styles.marginBottom,
        icon: 'icon-help',
        listSize: ControlledListItemSize.SMALL,
      },
    ];

    if (!isUserStudent()) {
      items.splice(1, 0, {
        id: NavMoreOptions.COLLABORATE,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        text: window.i18next.t('pmwjs_collaborate'),
        icon: 'icon-member-added',
        listSize: ControlledListItemSize.SMALL,
        className: styles.marginBottom,
      });
    }

    return items;
  };


  const onClickHandler = (id: NavMoreOptions): void => {
    onClose();
    switch (id) {
      case NavMoreOptions.CREATE_NEW:
        openNewDesign();
        break;
      case NavMoreOptions.OPEN_DESIGN:
        onClickOpenADesign();
        break;
      case NavMoreOptions.SETTINGS:
        onOpenSettingsModal();
        break;
      case NavMoreOptions.RESIZE:
        onOpenResizeModal();
        break;
      case NavMoreOptions.COLLABORATE:
        openCollaboratorsModal();
        break;
      case NavMoreOptions.DUPLICATE:
        copyPoster();
        break;
      case NavMoreOptions.LANGUAGE:
        onOpenLanguageModal();
        break;
      case NavMoreOptions.HELP:
        window.open('https://support.postermywall.com/hc/en-us/articles/360058560052?utm_source=editor&utm_medium=nav');
        break;
      default:
        throw new Error('unhandled option in more options panel');
    }
  };

  const getContent = (): ReactElement => {
    return (
      <div className={`spacing-p-t-1 spacing-p-b-4 spacing-p-r-4 spacing-p-l-4 ${styles.container}`}>
        <ControlledList className="spacing-m-0" onClick={onClickHandler} items={getItems()} />
      </div>
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
}
